import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useLocation, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { AuthModal } from './auth/AuthModal';
import { useUserSession } from './auth/UserSessionContext';
import { useUser } from './context/UserContext';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { UserProvider } from './context/UserContext';
import { PlanProvider } from './context/PlanContext';
import i18next from './i18n';
import { setupVoiceGlobals } from './voice/index';
import { initTelemetry, logEvent } from './telemetry';
import './auth/devAdminSeed';
import CommandRoomUsage from './components/CommandRoomUsage';
import RequireSession from './components/RequireSession';
import PolicyFooter from './components/PolicyFooter';
import TikTokPixelInit from './lib/TikTokPixelInit';
import MetaPixelInit from './lib/MetaPixelInit';
import GoogleAdsInit from './lib/GoogleAdsInit';
import { trackDemoGoogleLoginSuccess, trackMarketingPageView } from './lib/marketingEvents';

// ─── Code Splitting: lazy-load de todas las páginas ───
// Cada página se carga ON-DEMAND cuando el usuario navega a ella,
// reduciendo el bundle inicial en ~60-70%.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Chat = lazy(() => import('./pages/Chat'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const History = lazy(() => import('./pages/History'));
const Tools = lazy(() => import('./pages/Tools'));
const MyGPTs = lazy(() => import('./pages/MyGPTs'));
const Plans = lazy(() => import('./pages/Plans'));
const LifeCoach = lazy(() => import('./pages/LifeCoach'));
const StudyTutor = lazy(() => import('./pages/StudyTutor'));
const MassControl = lazy(() => import('./pages/MassControl'));
const DeepWeb = lazy(() => import('./pages/DeepWeb'));
const Connect = lazy(() => import('./pages/Connect'));
const Ideas = lazy(() => import('./pages/Ideas'));
const UserPanel = lazy(() => import('./pages/UserPanel'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Goodbye = lazy(() => import('./pages/Goodbye'));
const Admin = lazy(() => import('./pages/Admin'));
const BillingPortal = lazy(() => import('./pages/BillingPortal'));
const SupportPage   = lazy(() => import('./pages/SupportPage'));
const TermsPage     = lazy(() => import('./pages/TermsPage'));
const PrivacyPage   = lazy(() => import('./pages/PrivacyPage'));
const RefundPage    = lazy(() => import('./pages/RefundPage'));
const StudyTutorDemoLanding = lazy(() => import('./pages/marketing/StudyTutorDemoLanding'));
const DeepResearchDemoLanding = lazy(() => import('./pages/marketing/DeepResearchDemoLanding'));

// ─── Fallback de carga para Suspense ───
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="text-gray-400 text-sm animate-pulse">Cargando…</div>
    </div>
  );
}

// Ruta ligera para capturar referido y redirigir
const ReferralCapture = () => {
  const { code } = useParams();
  useEffect(()=>{ try { if(code){ const payload={ code, ts: Date.now() }; localStorage.setItem('ref_code_pending', JSON.stringify(payload)); localStorage.removeItem('ref_bonus_applied'); } } catch {} },[code]);
  return <Navigate to="/user" replace />;
};

const LegacyProjectRedirect = () => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace('/projects', '/codeproject-ai')}${search}${hash}`} replace />;
};

function PublicAuthPage() {
  const { t } = useTranslation();

  React.useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent('auth:open', { detail: { view: 'login', origin: 'public-login' } }));
    } catch {}
  }, []);

  return (
    <div className="min-h-screen bg-dark constellation-bg flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-xl bg-dark-light border border-gray-700 rounded-xl p-8 text-center">
        <h1 className="text-3xl font-bold text-white mb-3">IntelliVerseOne</h1>
        <p className="text-gray-300 mb-6">{t('auth.protected_route', 'Debes iniciar sesión para continuar')}</p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('auth:open', { detail: { view: 'login', origin: 'public-login.button' } }))}
            className="btn-primary"
          >
            {t('auth.login', 'Iniciar sesión')}
          </button>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('auth:open', { detail: { view: 'register', origin: 'public-register.button' } }))}
            className="btn-secondary"
          >
            {t('auth.signup', 'Registrarse')}
          </button>
        </div>
      </div>
      <AuthModal />
      <PolicyFooter />
    </div>
  );
}

function UserBox() {
  const { user, loading, logout } = useUserSession();
  const { user: profile } = useUser();
  const { t } = useTranslation();
  const [resendState,setResendState] = React.useState('idle'); // idle | sending | sent | error
  const emailConfirmed = !!user?.email_confirmed_at;
  const resend = async ()=>{
    if(resendState==='sending') return;
    setResendState('sending');
    try {
      const { error } = await import('./lib/supabaseClient').then(m=> m.supabase.auth.resend({ type:'signup', email: user.email }));
      if(error) throw error;
      setResendState('sent');
      setTimeout(()=> setResendState('idle'), 4000);
    } catch(err){ setResendState('error'); setTimeout(()=> setResendState('idle'), 4000); }
  };
  // Mostrar estado de carga solo si NO hay usuario aún y está cargando
  if (loading && !user) return <span className="text-xs text-gray-500">{t('common.loading')}</span>;
  // Si no hay usuario y no está cargando, mostrar acciones de login/signup
  if(!user) return <>
    <button onClick={()=> window.dispatchEvent(new CustomEvent('auth:open',{ detail:{ view:'login'} }))} className="btn-secondary mr-2">{t('auth.login')}</button>
    <button onClick={()=> window.dispatchEvent(new CustomEvent('auth:open',{ detail:{ view:'register'} }))} className="btn-primary">{t('auth.signup')}</button>
  </>;
  const planKey = (profile && profile.plan) || null;
  const trialDaysLeft = (profile && profile.trialDaysLeft) || 0;
  // ✅ FIX #5: label de plan maneja null (verificando...)
  const planLabel = planKey==='pro_plus' ? t('user.plan.pro_plus') : planKey==='pro' ? t('user.plan.pro') : planKey==='trial' ? t('user.plan.trial') : t('common.loading','Verificando…');
  return <div className="flex items-center gap-4">
    <div className="hidden sm:flex items-center gap-2 mr-2">
      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
        planKey==='pro_plus' ? 'bg-green-900/20 text-success border-green-600/30' :
        planKey==='pro' ? 'bg-yellow-900/20 text-warning border-yellow-600/30' :
        'bg-gray-800 text-gray-300 border-gray-600'
      }`} title={planKey==='trial' && trialDaysLeft>0 ? t('sidebar.days_left', { count: trialDaysLeft }) : undefined}>
        {planLabel}
      </span>
      {planKey==='trial' && trialDaysLeft>0 && (
        <span className="text-[10px] text-gray-400">{t('sidebar.days_left', { count: trialDaysLeft })}</span>
      )}
    </div>
    <div className="text-right">
      <div className="flex items-center justify-end gap-2">
        <span className="text-sm text-white truncate max-w-[140px]" title={user.email}>{user.email}</span>
        {!emailConfirmed && <span className="text-[10px] bg-yellow-600/30 text-yellow-300 px-2 py-0.5 rounded-full">{t('user.email.unverified')}</span>}
        {emailConfirmed && <span className="text-[10px] bg-green-600/30 text-green-300 px-2 py-0.5 rounded-full">{t('common.ok')}</span>}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-primary/70 text-right">{planLabel}</div>
      {!emailConfirmed && <div className="mt-1 flex justify-end">
        <button onClick={resend} className="text-[10px] underline decoration-dotted text-gray-400 hover:text-primary disabled:opacity-40" disabled={resendState==='sending' || resendState==='sent'}>
          {resendState==='idle' && t('auth.resend')}
          {resendState==='sending' && t('common.sending')}
          {resendState==='sent' && `${t('common.sent')} ✔`}
          {resendState==='error' && t('common.error')}
        </button>
      </div>}
    </div>
    <button onClick={logout} className="btn-secondary !px-3">{t('auth.logout')}</button>
  </div>;
}

// Wrapper de sección para registrar vistas (lo definimos antes de usarlo en JSX)
function SectionWrap({ name, children }){
  useEffect(()=>{ try { logEvent('view',{ section:name }); } catch {} }, [name]);
  return children;
}

function MarketingPageViewTracker() {
  const location = useLocation();
  const { user: sessionUser, loading } = useUserSession();
  const demoLoginTrackedRef = React.useRef('');

  useEffect(() => {
    try {
      trackMarketingPageView({
        pathname: `${location.pathname}${location.search || ''}`,
        language: i18next.language || 'en',
      });
    } catch {}
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (loading || !sessionUser?.id) return;
    const params = new URLSearchParams(location.search || '');
    const demo = params.get('demo_resume');
    if (demo !== 'study' && demo !== 'deep') return;
    const dedupeKey = `${demo}:${sessionUser.id}:${location.pathname}`;
    if (demoLoginTrackedRef.current === dedupeKey) return;
    demoLoginTrackedRef.current = dedupeKey;
    try {
      trackDemoGoogleLoginSuccess({
        demo,
        language: params.get('lang') || i18next.language || 'en',
      });
    } catch {}
  }, [loading, sessionUser?.id, location.pathname, location.search]);

  return null;
}

function RoutedAppSurface({ sidebarOpen, setSidebarOpen, isAuthenticated, t }) {
  const location = useLocation();
  const isDemoRoute = location.pathname === '/demo-study-tutor' || location.pathname === '/demo-deep-research';

  if (isDemoRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/demo-study-tutor" element={<StudyTutorDemoLanding />} />
          <Route path="/demo-deep-research" element={<DeepResearchDemoLanding />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-dark constellation-bg">
      <div className="flex">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <main className={`flex-1 transition-all duration-300 overflow-x-hidden ${
          sidebarOpen ? 'ml-0 md:ml-64' : 'ml-0 md:ml-16'
        }`}>
          <div className="flex items-center justify-between px-4 md:px-6 pt-4 min-h-[60px]">
            <button
              className="md:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Abrir menÃº"
            >
              <span className="text-2xl leading-none">â˜°</span>
            </button>
            <div className="flex-1" />
            <UserBox />
          </div>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Rutas de autenticaciÃ³n */}
            <Route path="/login"   element={isAuthenticated ? <Navigate to="/" replace /> : <PublicAuthPage />} />
            <Route path="/signup"  element={isAuthenticated ? <Navigate to="/" replace /> : <PublicAuthPage />} />
            {/* Legal / pÃºblicas */}
            <Route path="/goodbye" element={<Goodbye />} />
            <Route path="/terms"   element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund"  element={<RefundPage />} />
            {/* Feature routes â€” abiertas a todos; acciones gateadas con useRequireAuthAction */}
            <Route path="/"            element={<SectionWrap name="dashboard"><Dashboard /></SectionWrap>} />
            <Route path="/chat"        element={<SectionWrap name="chat"><Chat /></SectionWrap>} />
            <Route path="/codeproject-ai" element={<SectionWrap name="projects"><Projects /></SectionWrap>} />
            <Route path="/codeproject-ai/:id" element={<SectionWrap name="project-detail"><ProjectDetail /></SectionWrap>} />
            <Route path="/projects"    element={<Navigate to="/codeproject-ai" replace />} />
            <Route path="/projects/:id" element={<LegacyProjectRedirect />} />
            <Route path="/history"     element={<SectionWrap name="history"><History /></SectionWrap>} />
            <Route path="/tools"       element={<SectionWrap name="tools"><Tools /></SectionWrap>} />
            <Route path="/deep-research" element={<SectionWrap name="deepweb"><DeepWeb /></SectionWrap>} />
            <Route path="/deepweb"     element={<Navigate to="/deep-research" replace />} />
            <Route path="/connect-by-interests" element={<SectionWrap name="connect"><Connect /></SectionWrap>} />
            <Route path="/connect"     element={<Navigate to="/connect-by-interests" replace />} />
            <Route path="/ideas-and-projects" element={<SectionWrap name="ideas"><Ideas /></SectionWrap>} />
            <Route path="/ideas"       element={<Navigate to="/ideas-and-projects" replace />} />
            <Route path="/propose"     element={<Navigate to="/ideas-and-projects" replace />} />
            <Route path="/proposals"   element={<Navigate to="/ideas-and-projects" replace />} />
            <Route path="/my-gpts"     element={<SectionWrap name="my-gpts"><MyGPTs /></SectionWrap>} />
            <Route path="/plans"       element={<SectionWrap name="plans"><Plans /></SectionWrap>} />
            <Route path="/support"     element={<SectionWrap name="support"><SupportPage /></SectionWrap>} />
            <Route path="/personal-ai-plan" element={<SectionWrap name="life-coach"><LifeCoach /></SectionWrap>} />
            <Route path="/life-coach"  element={<Navigate to="/personal-ai-plan" replace />} />
            <Route path="/study-ai-tutor" element={<SectionWrap name="study-tutor"><StudyTutor /></SectionWrap>} />
            <Route path="/study-tutor" element={<Navigate to="/study-ai-tutor" replace />} />
            <Route path="/community-impact-ai" element={<SectionWrap name="mass-control"><MassControl /></SectionWrap>} />
            <Route path="/mass-control" element={<Navigate to="/community-impact-ai" replace />} />
            <Route path="/ref/:code"   element={<ReferralCapture />} />
            {/* Rutas privadas â€” requieren sesiÃ³n activa */}
            <Route path="/user"    element={<RequireSession><SectionWrap name="user"><UserPanel /></SectionWrap></RequireSession>} />
            <Route path="/admin"   element={<RequireSession><SectionWrap name="admin"><Admin /></SectionWrap></RequireSession>} />
            <Route path="/billing" element={<RequireSession><SectionWrap name="billing"><BillingPortal /></SectionWrap></RequireSession>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <div id="aria-live-global" aria-live="polite" className="sr-only" />
          {typeof window !== 'undefined' && window.__PLAN_LIMITS_FALLBACK__ && (
            <div className="fixed bottom-4 right-4 bg-warning/20 text-warning text-xs px-3 py-2 rounded border border-warning/40 shadow-lg" role="status" title={t('plan.fallback_notice')}>
              âš ï¸ <span>{t('plan.fallback_notice')}</span>
            </div>
          )}
          <PolicyFooter />
        </main>
        <CommandRoomUsage />
      </div>
    </div>
  );
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const { t } = useTranslation();
  const { user: sessionUser, loading: authLoading } = useUserSession();
  const isAuthenticated = !!sessionUser;

  // Atajo de desarrollo: Ctrl+Shift+L limpia sesión de Supabase (sb-*) y recarga
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC')>=0;
      const ctrl = isMac ? e.metaKey : e.ctrlKey;
      if (ctrl && e.shiftKey && e.key.toLowerCase() === 'l') {
        try {
          Object.keys(localStorage).filter(k=>k.startsWith('sb-')).forEach(k=> localStorage.removeItem(k));
          document.cookie.split(';').forEach(c=>{ const n=c.split('=')[0].trim(); if(n.startsWith('sb-')) document.cookie = `${n}=; Max-Age=0; path=/; SameSite=Lax`; });
        } catch {}
        window.location.reload();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Exponer idioma UI para la orquestación (traducción de respuestas)
  useEffect(() => {
    try { window.__UI_LANG__ = i18next.language || 'en'; } catch {}
    const onLang = (lng) => { try { window.__UI_LANG__ = lng || 'en'; } catch {} };
    i18next.on('languageChanged', onLang);
    return () => { i18next.off('languageChanged', onLang); };
  }, []);

  

  // Inicializar capacidades de voz (V3) de manera opt‑in por eventos
  useEffect(() => {
    setupVoiceGlobals();
  }, []);

  // Telemetría básica
  useEffect(() => { initTelemetry(); }, []);

  // Safety timeout: si loading tarda más de 8s, ofrecer reintento
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  useEffect(() => {
    if (authLoading && !sessionUser) {
      setLoadingTimedOut(false);
      const timer = setTimeout(() => {
        console.warn('[App] ⚠️ Auth loading timed out after 8s');
        setLoadingTimedOut(true);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [authLoading, sessionUser]);

  if (authLoading && !sessionUser) {
    return (
      <div className="min-h-screen bg-dark constellation-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-300 text-sm">{t('common.loading')}</div>
          {loadingTimedOut && (
            <div className="mt-4">
              <p className="text-gray-500 text-xs mb-2">{t('common.loading_slow', 'La carga está tardando más de lo esperado.')}</p>
              <button onClick={() => window.location.reload()} className="text-primary text-xs underline hover:text-primary-light">
                {t('common.retry', 'Reintentar')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <UserProvider>
      <PlanProvider>
        <Router>
          {/* Layout unificado: guests y usuarios autenticados ven la misma interfaz.
              Las acciones están gateadas en cada página con useRequireAuthAction.
              Solo /user, /admin y /billing requieren sesión (datos privados). */}
          <AuthModal />
          <TikTokPixelInit />
          <MetaPixelInit />
          <GoogleAdsInit />
          <MarketingPageViewTracker />
          <RoutedAppSurface
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            isAuthenticated={isAuthenticated}
            t={t}
          />
        </Router>
      </PlanProvider>
    </UserProvider>
  );
}

export default App;
